/**
 * TableTile — columns and rows an agent filled (trains, prices, results).
 *
 * data: { columns: string[]; rows: (string|number|boolean|null)[][]; caption? }
 *
 * Click a header to sort by it (again to reverse): the point of a table
 * beside the chat is reading it your way, not the order the agent chose.
 */
import * as React from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import type { TileBodyProps } from "../registry";

type Cell = string | number | boolean | null;

interface TableData {
  columns?: string[];
  rows?: Cell[][];
  caption?: string;
}

function show(cell: Cell): string {
  if (cell === null || cell === undefined || cell === "") return "—";
  if (typeof cell === "boolean") return cell ? "yes" : "no";
  return String(cell);
}

function compare(a: Cell, b: Cell): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return show(a).localeCompare(show(b), undefined, { numeric: true });
}

export function TableTile({ data }: TileBodyProps) {
  const d = data as TableData;
  const columns = d.columns ?? [];
  const [sort, setSort] = React.useState<{ col: number; dir: 1 | -1 } | null>(null);
  const rows = React.useMemo(() => {
    const all = d.rows ?? [];
    if (!sort) return all;
    return [...all].sort((a, b) => compare(a[sort.col], b[sort.col]) * sort.dir);
  }, [d.rows, sort]);

  if (columns.length === 0) {
    return <p className="text-[12.5px] text-fg-faint">No columns.</p>;
  }

  const toggle = (col: number) =>
    setSort((s) => (s?.col === col ? (s.dir === 1 ? { col, dir: -1 } : null) : { col, dir: 1 }));

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse text-[12.5px]">
          <thead className="sticky top-0 bg-bg-elevated">
            <tr>
              {columns.map((c, i) => (
                <th
                  key={i}
                  scope="col"
                  aria-sort={sort?.col === i ? (sort.dir === 1 ? "ascending" : "descending") : "none"}
                  className="border-b border-border-strong p-0 text-left font-mono text-[10px] uppercase tracking-[0.12em] text-fg-faint"
                >
                  <button
                    type="button"
                    onClick={() => toggle(i)}
                    className="flex w-full items-center gap-1 px-2 py-1.5 text-left hover:text-fg"
                  >
                    {c}
                    {sort?.col === i &&
                      (sort.dir === 1 ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r} className="border-b border-border last:border-b-0 hover:bg-bg-sunken/60">
                {columns.map((_, c) => (
                  <td
                    key={c}
                    className={
                      typeof row[c] === "number"
                        ? "px-2 py-1.5 text-right font-mono tabular-nums text-fg"
                        : "px-2 py-1.5 text-fg"
                    }
                  >
                    {show(row[c] ?? null)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="px-2 py-2 text-[12.5px] text-fg-faint">No rows.</p>}
      </div>
      {d.caption && <p className="flex-shrink-0 text-[11.5px] text-fg-muted">{d.caption}</p>}
    </div>
  );
}
